import { db } from '@/utils/firebase';
import { logError, logInfo } from '@/utils/logger';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
export interface RoadmapItem {
  id?: string;
  title: string;
  description: string;
  eta?: Timestamp;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}
class RoadmapService {
  static ROADMAP_COLLECTION = 'roadmap';
  static async fetchRoadmapItems(): Promise<RoadmapItem[]> {
    try {
      const roadmapRef = collection(db, this.ROADMAP_COLLECTION);
      const q = query(roadmapRef, orderBy('eta', 'asc'));
      const querySnapshot = await getDocs(q);
      const roadmapItems: RoadmapItem[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as Omit<RoadmapItem, 'id'>;
        roadmapItems.push({
          ...data,
          id: doc.id,
        });
      });
      logInfo(
        'Roadmap items fetched successfully',
        'RoadmapService.fetchRoadmapItems',
        {
          count: roadmapItems.length,
        },
      );
      return roadmapItems;
    } catch (error) {
      logError(error, 'RoadmapService.fetchRoadmapItems');
      throw error;
    }
  }
  static async addRoadmapItem(
    item: Omit<RoadmapItem, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<string> {
    try {
      const roadmapRef = collection(db, this.ROADMAP_COLLECTION);
      const dataToAdd = {
        title: item.title,
        description: item.description,
        ...(item.eta && { eta: item.eta }),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      const docRef = await addDoc(roadmapRef, dataToAdd);
      logInfo(
        'Roadmap item added successfully',
        'RoadmapService.addRoadmapItem',
        {
          id: docRef.id,
          item,
        },
      );
      return docRef.id;
    } catch (error) {
      logError(error, 'RoadmapService.addRoadmapItem');
      throw error;
    }
  }
  static async updateRoadmapItem(
    id: string,
    item: Partial<Omit<RoadmapItem, 'id' | 'createdAt' | 'updatedAt'>>,
  ): Promise<void> {
    try {
      const roadmapItemRef = doc(db, this.ROADMAP_COLLECTION, id);
      const dataToUpdate: Partial<RoadmapItem> & { updatedAt: Timestamp } = {
        ...(item.title && { title: item.title }),
        ...(item.description && { description: item.description }),
        ...(item.eta && { eta: item.eta }),
        updatedAt: Timestamp.now(),
      };
      await updateDoc(roadmapItemRef, dataToUpdate);
      logInfo(
        'Roadmap item updated successfully',
        'RoadmapService.updateRoadmapItem',
        {
          id,
          updates: item,
        },
      );
    } catch (error) {
      logError(error, 'RoadmapService.updateRoadmapItem');
      throw error;
    }
  }
  static async deleteRoadmapItem(id: string): Promise<void> {
    try {
      const roadmapItemRef = doc(db, this.ROADMAP_COLLECTION, id);
      await deleteDoc(roadmapItemRef);
      logInfo(
        'Roadmap item deleted successfully',
        'RoadmapService.deleteRoadmapItem',
        {
          id,
        },
      );
    } catch (error) {
      logError(error, 'RoadmapService.deleteRoadmapItem');
      throw error;
    }
  }
}
export default RoadmapService;
